import React, { useEffect, useState } from 'react';
import { useEnvironment } from '@/hooks/useEnvironment';
import { getMyIssues } from '@/services/authService';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertCircle, FileText, Inbox, FileEdit } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiState } from '@/hooks/useApiState';
import { useLanguage } from '@/hooks/useLanguage';

export const MyIssuesSection = () => {
  const { isNativeWebView } = useEnvironment();
  const { loading, setLoading, error, setError } = useApiState();
  const { toast } = useToast();
  const [issues, setIssues] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    getMyIssues().then(({ data, status, message }) => {
      if (status === 200) {
        if (Array.isArray(data) && data.length > 0) {
          setIssues(data);
        } else if (data && data.complaints && data.complaints.length > 0) {
          setIssues(data.complaints);
        } else {
          setIssues([]);
        }
        setError('');
      } else if (status === 204) {
        setIssues([]);
        setError('');
      } else {
        const userMessage = mapMyIssuesError(status, message);
        setError(userMessage);
        toast({
          title: 'Error',
          description: userMessage,
          variant: 'destructive',
          duration: 5000,
        });
      }
      setLoading(false);
    });
  }, []);

  // Map API error/status to user-friendly message
  function mapMyIssuesError(status, message) {
    
    if (status === 404) return 'No account found. Please check your profile or contact support.';
    if (status === 500) return 'A server error occurred. Please try again later.';
    if (status === 504) return 'The request timed out. Please try again later.';
    
    if (message && message.toLowerCase().includes('no complaints')) return 'You have no complaints or issues.';
    return 'Unable to load your issues at this time. Please try again later.';
  }

  // Helper to extract answer by qCode
  function getAnswer(answers, code) {
    if (!Array.isArray(answers)) return '';
    const found = answers.find(a => a.qCode === code);
    return found ? found.ans : '';
  }

  // Web UI
  const WebDesign = () => (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-white py-12 px-2">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-2 mb-2">
            <FileEdit className="w-8 h-8 text-orange-400" />
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('title', 'myIssues')}</h1>
          </div>
          <p className="text-gray-600 text-lg text-center max-w-xl">
            {t('subtitle', 'myIssues')}
          </p>
        </div>
        <div className="space-y-6">
          {issues && issues.length > 0 ? (
            issues.map((issue, idx) => {
              const id = issue.complaintId || issue.id || `#${idx+1}`;
              const status = (issue.status || '').toLowerCase();
              const filedOn = issue.submittedOn ? new Date(issue.submittedOn).toLocaleDateString() : '';
              const type = getAnswer(issue.answers, 'type');
              const urgency = getAnswer(issue.answers, 'urg');
              const desc = getAnswer(issue.answers, 'desc');
              const details = getAnswer(issue.answers, 'details');
              let statusColor = 'text-orange-500';
              let statusIcon = <AlertCircle className="inline w-5 h-5 mr-1" />;
              let statusText = t('inProgress', 'myIssues');
              if (status === 'resolved' || status === 'closed') {
                statusColor = 'text-green-600';
                statusIcon = <Inbox className="inline w-5 h-5 mr-1" />;
                statusText = t('resolved', 'myIssues');
              }
              return (
                <section key={id} className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-2 border-l-4 border-orange-400">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-base text-gray-900">#{id}</span>
                    {urgency && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gray-200 text-gray-700">{urgency}</span>}
                  </div>
                  <div className="font-medium text-lg text-gray-800 leading-tight">{type}</div>
                  <div className="text-sm text-muted-foreground mb-1">{desc}</div>
                  {details && <div className="text-xs text-gray-500">{details}</div>}
                  {filedOn && <div className="text-xs text-gray-500">{t('filedOn', 'myIssues')} {filedOn}</div>}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`flex items-center font-medium ${statusColor}`}>{statusIcon}{statusText}</span>
                  </div>
                </section>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Inbox className="w-16 h-16 text-gray-300 mb-4" />
              <div className="text-center text-muted-foreground text-lg font-medium">{t('noIssues', 'myIssues')}</div>
              <div className="text-center text-gray-400 text-base mt-2">{t('noIssuesDesc', 'myIssues')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Mobile UI: iPhone-style, modern, clean, edge-to-edge
  const WebViewDesign = () => (
    <div className="bg-gray-50 min-h-screen pb-4">
      <div className="sticky top-0 z-10 bg-gray-50 flex justify-between items-center px-4 py-3 border-b border-gray-200">
        {/* <div className="font-semibold text-lg text-gray-900">{t('title', 'myIssues')}</div> */}
      </div>
      <div className="space-y-4 px-2 pt-2">
        {issues && issues.length > 0 ? (
          issues.map((issue, idx) => {
            const id = issue.complaintId || issue.id || `#${idx+1}`;
            const status = (issue.status || '').toLowerCase();
            const filedOn = issue.submittedOn ? new Date(issue.submittedOn).toLocaleDateString() : '';
            const type = getAnswer(issue.answers, 'type');
            const urgency = getAnswer(issue.answers, 'urg');
            const desc = getAnswer(issue.answers, 'desc');
            const details = getAnswer(issue.answers, 'details');
            let statusColor = 'bg-orange-100 text-orange-700 border-orange-300';
            let statusIcon = <AlertCircle className="inline w-5 h-5 mr-1 text-orange-500" />;
            let statusText = t('inProgress', 'myIssues');
            if (status === 'resolved' || status === 'closed') {
              statusColor = 'bg-green-100 text-green-700 border-green-300';
              statusIcon = <Inbox className="inline w-5 h-5 mr-1 text-green-500" />;
              statusText = t('resolved', 'myIssues');
            }
            return (
              <section key={id} className="bg-white rounded-2xl shadow-md p-4 flex flex-col gap-2 border-l-4 border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-base text-gray-900">#{id}</span>
                  {urgency && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-blue-100 text-blue-700">{urgency}</span>}
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold flex items-center border ${statusColor}`}>{statusIcon}{statusText}</span>
                </div>
                <div className="font-medium text-lg text-gray-800 leading-tight">{type}</div>
                <div className="text-sm text-muted-foreground mb-1">{desc}</div>
                {details && <div className="text-xs text-gray-500">{details}</div>}
                {filedOn && <div className="text-xs text-gray-400 mt-1">{t('filedOn', 'myIssues')} {filedOn}</div>}
              </section>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <Inbox className="w-16 h-16 text-gray-300 mb-4" />
            <div className="text-center text-muted-foreground text-lg font-medium">{t('noIssues', 'myIssues')}</div>
            <div className="text-center text-gray-400 text-base mt-2">{t('noIssuesDesc', 'myIssues')}</div>
          </div>
        )}
      </div>
    </div>
  );

  // Mobile UI: Stacked, touch-friendly layout
  const WebViewSkeleton = () => (
    <Card className="mx-2">
      <CardHeader />
      <CardContent>
        {[1,2,3].map(i => (
          <Card key={i} className="mb-4">
            <CardContent>
              <Skeleton className="h-4 w-1/3 mb-2" />
              <Skeleton className="h-5 w-2/3 mb-2" />
              <Skeleton className="h-4 w-1/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );

  if (loading) {
    if (isNativeWebView) return <WebViewSkeleton />;
    return <div className="p-8 text-center text-muted-foreground">{t('loading', 'common')}</div>;
  }
  if (error) return <div className="p-8 text-center text-destructive">{error}</div>;
  return isNativeWebView ? <WebViewDesign /> : <WebDesign />;
};

export default MyIssuesSection;